/**
 * Resting BMR helpers — Mifflin–St Jeor + extract from biometrics JSON.
 */
/** Mifflin–St Jeor resting energy (kcal/day). */
export function estimateRestingBmrMifflinStJeor(params) {
    const { weightKg, heightCm, ageYears, isMale } = params;
    if (!Number.isFinite(weightKg) ||
        weightKg <= 0 ||
        !Number.isFinite(heightCm) ||
        heightCm <= 0 ||
        !Number.isFinite(ageYears) ||
        ageYears <= 0) {
        return null;
    }
    return Math.round(10 * weightKg + 6.25 * heightCm - 5 * ageYears + (isMale ? 5 : -161));
}
/** Prefer bodyCompositionSnapshot.bmr, then top-level measurements.bmr. */
export function extractBmrFromMeasurements(measurements) {
    if (!measurements || typeof measurements !== 'object') {
        return null;
    }
    const snapshot = measurements.bodyCompositionSnapshot;
    const fromSnap = Number(snapshot?.bmr);
    if (Number.isFinite(fromSnap) && fromSnap > 0) {
        return Math.round(fromSnap);
    }
    const top = Number(measurements.bmr);
    if (Number.isFinite(top) && top > 0) {
        return Math.round(top);
    }
    return null;
}
export function extractWeightKgFromMeasurements(measurements) {
    if (!measurements || typeof measurements !== 'object') {
        return null;
    }
    const w = Number(measurements.weight);
    if (!Number.isFinite(w) || w <= 0) {
        return null;
    }
    return w;
}
/** Profile existingWeight is stored in pounds. */
export function lbsToKg(lbs) {
    return lbs / 2.20462;
}
export function ageYearsFromBirthDate(birthDate, onDate) {
    if (!birthDate || typeof birthDate !== 'string') {
        return null;
    }
    const parsed = new Date(birthDate);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    let age = onDate.getFullYear() - parsed.getFullYear();
    const m = onDate.getMonth() - parsed.getMonth();
    if (m < 0 || (m === 0 && onDate.getDate() < parsed.getDate())) {
        age -= 1;
    }
    if (age <= 0 || age > 120) {
        return null;
    }
    return age;
}
export function isMaleGender(gender) {
    if (gender == null) {
        return null;
    }
    const g = String(gender).trim().toUpperCase();
    if (g === 'M' || g === 'MALE') {
        return true;
    }
    if (g === 'F' || g === 'FEMALE') {
        return false;
    }
    return null;
}
/**
 * Resolve resting BMR from scale snapshot or profile estimate inputs.
 * Caller supplies already-fetched scale BMR / profile fields.
 */
export function resolveRestingBmr(params) {
    if (params.scaleBmr != null &&
        Number.isFinite(params.scaleBmr) &&
        params.scaleBmr > 0) {
        return { restingBmr: Math.round(params.scaleBmr), source: 'scale' };
    }
    if (params.isMale == null) {
        return { restingBmr: null, source: null };
    }
    const estimated = estimateRestingBmrMifflinStJeor({
        weightKg: Number(params.weightKg),
        heightCm: Number(params.heightCm),
        ageYears: Number(params.ageYears),
        isMale: params.isMale,
    });
    if (estimated == null) {
        return { restingBmr: null, source: null };
    }
    return { restingBmr: estimated, source: 'profile' };
}
